import { useCallback, useEffect, useRef } from 'react';
import EditorJS, { BlockToolData, OutputData } from '@editorjs/editorjs';
import { uploadFile } from '@/data/services/fileService';
import { ImageFolderEnum } from '@/models/CkEditor';

type UseEditorClipboardProps = {
  editorInstance: React.MutableRefObject<EditorJS | null>;
  folder: ImageFolderEnum;
  extraBody?: object;
  readOnly?: boolean;
  element: HTMLElement | null;
  onChange?: (data: OutputData) => void;
};

type SavedData = {
  id: string;
  tool: string;
  data: BlockToolData;
  time: number;
};

type ImageBlockData = {
  file: { url: string };
  caption?: string;
  withBorder?: boolean;
  stretched?: boolean;
  withBackground?: boolean;
};

type ImageSavedData = SavedData & {
  tool: 'image';
  data: ImageBlockData;
};

const isImageBlock = (block: SavedData): block is ImageSavedData => {
  return block.tool === 'image' && !!block.data?.file?.url;
};

export const useEditorClipboard = ({
  editorInstance,
  folder,
  extraBody,
  readOnly,
  element,
  onChange,
}: UseEditorClipboardProps) => {
  const clipboardData = useRef<SavedData | null>(null);

  const getCurrentBlock = useCallback(async () => {
    if (!editorInstance.current) return null;

    const currentIndex = editorInstance.current.blocks.getCurrentBlockIndex();
    if (currentIndex < 0) return null;

    const blockApi =
      editorInstance.current.blocks.getBlockByIndex(currentIndex);
    if (!blockApi) return null;

    const savedData = await blockApi.save();
    if (!savedData) return null;

    return { blockApi, savedData, index: currentIndex };
  }, [editorInstance]);

  const uploadImage = useCallback(
    async (file: File): Promise<string | null> => {
      try {
        const { image } = await uploadFile({
          file,
          folder,
          extraBody,
        });
        return image;
      } catch {
        return null;
      }
    },
    [folder, extraBody],
  );

  const handleCut = useCallback(
    async (e: KeyboardEvent) => {
      if (readOnly) return;
      if (!editorInstance.current) return;

      const blockInfo = await getCurrentBlock();

      if (!blockInfo) return;

      const { savedData, index } = blockInfo;

      if (!isImageBlock(savedData)) return;

      e.preventDefault();
      e.stopPropagation();

      clipboardData.current = savedData;

      const imageUrl = savedData.data.file.url;
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();

        await navigator.clipboard.write([
          new ClipboardItem({
            [blob.type]: blob,
          }),
        ]);
      } catch {
        await navigator.clipboard.writeText(imageUrl);
      }

      editorInstance.current.blocks.delete(index);

      const newData = await editorInstance.current.save();
      onChange?.(newData);
    },
    [editorInstance, getCurrentBlock, readOnly, onChange],
  );

  const handlePaste = useCallback(
    async (e: KeyboardEvent) => {
      if (readOnly) return;
      if (!editorInstance.current) return;

      let imageFile: File | null = null;

      try {
        const items = await navigator.clipboard.read();

        for (const item of items) {
          const imageType = item.types.find(type => type.startsWith('image/'));
          if (imageType) {
            const blob = await item.getType(imageType);
            imageFile = new File(
              [blob],
              `pasted-image-${Date.now()}.${imageType.split('/')[1]}`,
              { type: imageType },
            );
            break;
          }
        }
      } catch {}

      if (!imageFile) {
        if (clipboardData.current && isImageBlock(clipboardData.current)) {
          e.preventDefault();
          e.stopPropagation();

          const currentIndex =
            editorInstance.current.blocks.getCurrentBlockIndex();
          const insertIndex = currentIndex >= 0 ? currentIndex + 1 : undefined;

          editorInstance.current.blocks.insert(
            'image',
            clipboardData.current.data,
            undefined,
            insertIndex,
            true,
          );

          const newData = await editorInstance.current.save();
          onChange?.(newData);

          clipboardData.current = null;
          return;
        }
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const imageUrl = await uploadImage(imageFile);
      if (!imageUrl) return;

      const currentIndex = editorInstance.current.blocks.getCurrentBlockIndex();
      const insertIndex = currentIndex >= 0 ? currentIndex + 1 : undefined;

      editorInstance.current.blocks.insert(
        'image',
        {
          file: { url: imageUrl },
          caption: '',
          withBorder: false,
          stretched: false,
          withBackground: false,
        } as ImageBlockData,
        undefined,
        insertIndex,
        true,
      );

      const newData = await editorInstance.current.save();
      onChange?.(newData);
    },
    [editorInstance, readOnly, uploadImage, onChange],
  );

  const handleKeyDown = useCallback(
    async (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;

      if (e.key === 'x' || e.key === 'X') {
        await handleCut(e);
      } else if (e.key === 'v' || e.key === 'V') {
        const blockInfo = await getCurrentBlock();
        if (blockInfo && isImageBlock(blockInfo.savedData)) {
          return;
        }
        await handlePaste(e);
      }
    },
    [handleCut, handlePaste, getCurrentBlock],
  );

  useEffect(() => {
    if (!element || readOnly) return;

    element.addEventListener('keydown', handleKeyDown, true);

    return () => {
      element.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [element, handleKeyDown, readOnly]);

  return { clipboardData };
};
